import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItemRadio,
  makeStyles,
  mergeClasses,
  tokens,
} from '@coreai/fluentui-react';
import { ChevronDown16Regular } from '@fluentui/react-icons';
import {
  useDraftModeStore,
  selectIsDraftMode,
  selectDraftStatus,
  selectSaveError,
} from '@/stores/draftModeStore';
import {
  useUIPreferencesStore,
  getDraftAutosaveOption,
  DRAFT_AUTOSAVE_OPTIONS,
  type DraftAutosaveMode,
} from '@/stores/uiPreferencesStore';
import { telemetryService } from '@/services/telemetryService';
import { visuallyHiddenStyle } from '@/styles/a11y';

const AUTOSAVE_RADIO_NAME = 'draft-autosave-mode';

const useStyles = makeStyles({
  trigger: {
    // Tabular numerals keep the label width stable across "30s" vs "5 min".
    fontVariantNumeric: 'tabular-nums',
    columnGap: tokens.spacingHorizontalXS,
  },
  triggerOff: {
    color: tokens.colorNeutralForeground3,
  },
  srOnly: visuallyHiddenStyle,
});

export const AutosavePill = () => {
  const styles = useStyles();

  const isDraftMode = useDraftModeStore(selectIsDraftMode);
  const draftStatus = useDraftModeStore(selectDraftStatus);
  const saveError = useDraftModeStore(selectSaveError);

  const draftAutosaveMode = useUIPreferencesStore((s) => s.draftAutosaveMode);
  const setDraftAutosaveMode = useUIPreferencesStore((s) => s.setDraftAutosaveMode);

  const handleModeChange = useCallback(
    (mode: DraftAutosaveMode) => {
      if (mode === draftAutosaveMode) return;
      telemetryService.trackEvent('workflow.draft.autosave_mode_changed', {
        from: draftAutosaveMode,
        to: mode,
        source: 'autosave-pill',
      });
      setDraftAutosaveMode(mode);
    },
    [draftAutosaveMode, setDraftAutosaveMode],
  );

  // Screen-reader announcements for autosave state transitions. aria-label
  // changes on the trigger button aren't announced unless focus moves to
  // it, so we surface state through dedicated live regions instead. Reset
  // back to '' on the next render after each announcement so repeated
  // saves re-announce (SRs ignore identical consecutive content).
  const [politeMessage, setPoliteMessage] = useState('');
  const lastStatusRef = useRef<typeof draftStatus | null>(null);
  useEffect(() => {
    const prev = lastStatusRef.current;
    lastStatusRef.current = draftStatus;
    if (prev === draftStatus) return;
    if (draftStatus === 'saving') {
      setPoliteMessage('Saving draft');
    } else if (draftStatus === 'saved') {
      setPoliteMessage('Draft saved');
    } else {
      return;
    }
    const id = window.setTimeout(() => setPoliteMessage(''), 1500);
    return () => window.clearTimeout(id);
  }, [draftStatus]);

  // Only meaningful while authoring a draft.
  if (!isDraftMode) return null;

  const option = getDraftAutosaveOption(draftAutosaveMode);
  const isOff = !option.enabled;

  return (
    <>
      <Menu
        checkedValues={{ [AUTOSAVE_RADIO_NAME]: [draftAutosaveMode] }}
        onCheckedValueChange={(_, data) => {
          if (data.name === AUTOSAVE_RADIO_NAME && data.checkedItems.length > 0) {
            handleModeChange(data.checkedItems[0] as DraftAutosaveMode);
          }
        }}
      >
        <MenuTrigger disableButtonEnhancement>
          <Button
            appearance="subtle"
            size="small"
            className={mergeClasses(styles.trigger, isOff && styles.triggerOff)}
            aria-label={`Autosave: ${option.shortLabel}`}
            icon={<ChevronDown16Regular />}
            iconPosition="after"
          >
            {`Autosave: ${option.shortLabel}`}
          </Button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {DRAFT_AUTOSAVE_OPTIONS.map((opt) => (
              <MenuItemRadio key={opt.mode} name={AUTOSAVE_RADIO_NAME} value={opt.mode}>
                {opt.label}
              </MenuItemRadio>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>
      <span role="status" aria-live="polite" className={styles.srOnly}>
        {politeMessage}
      </span>
      {saveError && (
        <span role="alert" className={styles.srOnly}>
          {`Autosave failed: ${saveError}`}
        </span>
      )}
    </>
  );
};

export default AutosavePill;
